// =====================================================================
// src/modulos/precheckin.js — Pré-check-in: o lado da recepção
//
// O painel (precheckin_painel, 0018): cada reserva confirmada por terminar
// com o estado do seu link e do seu envio. Gerar o link (o banco devolve o
// token uma vez; a tela monta o endereço e mostra para copiar — canal
// `copiado`, decisão do Hugo) e conferir o que o hóspede mandou lado a lado
// com a ficha. Quem só VÊ enxerga sem botões.
//
// 0059 (Hugo, 24/09): o link é do DONO da reserva — proprietário ou
// imobiliária; a equipe do hotel só gera para reserva sem
// imobiliária. E não há mais "Recusar": se o envio tem erro, a recepção
// confere e corrige a ficha no check-in, que já edita.
// =====================================================================
import { hojeDe } from '../lib/contexto.js';
import { el, icone, abrirModal, fecharModal, aviso, confirmar, enquantoAtualiza } from '../ui.js';
import {
  ESTADOS, ABAS_PRECHECKIN, classificarPainel, filtrarPainel, resumoPainel, acoesDaLinha, compararEnvio, montarLink, rotuloFaixa, resumoViagem, rotuloSexo, rotuloRaca, rotuloDeficiencia, rotuloTipoDeficiencia,
  dataCurta, dataISO, noites, formatarDocumento, idade,
} from '../lib/precheckin.js';

export function montar(cont, { auth, contexto, agora = () => Date.now() }) {
  const nivelDe = ch => (contexto.modulos.find(m => m.chave === ch) || {}).nivel;
  const podeEditar = nivelDe('precheckin') === 'editar';
  const sistema = !!(contexto.usuario && contexto.usuario.sistema);
  const souDono = sistema || !!contexto.imobiliaria;   // 0059: quem gera o link
  let vivo = true;
  let linhas = [], clientes = [];
  let clienteSel = sistema ? null : contexto.cliente.id;
  let filtro = { aba: 'pendentes', texto: '' };
  const hoje = () => hojeDe(contexto, agora());   // o dia do hotel (0020), não o do aparelho
  const agoraISO = () => new Date(agora()).toISOString();
  const pc = () => (sistema ? { p_cliente: clienteSel } : {});

  async function carregar() {
    if (sistema) { clientes = await auth.ler('clientes', 'select=id,slug,nome&order=nome'); if (!clienteSel && clientes.length) clienteSel = clientes[0].id; }
    if (!clienteSel) { linhas = []; return; }
    linhas = await auth.rpc('precheckin_painel', { ...pc() });
  }
  // enquantoAtualiza: o <html> conta os ciclos, e a prova espera o contador
  // crescer em vez de adivinhar que a tela ja se refez (ui.js).
  function recarregar() {
    return enquantoAtualiza(async () => {
      try { await carregar(); } catch (e) { aviso(e.message, 'erro'); return; }
      desenhar();
    });
  }

  function desenhar() {
    if (!vivo) return;
    cont.replaceChildren();
    cont.dataset.tela = 'precheckin';
    const lista = classificarPainel(linhas, agoraISO());
    const r = resumoPainel(lista);
    cont.appendChild(el('div', { class: 'gov-cabeca' },
      el('div', { class: 'usu-filtros', style: { marginBottom: '0' } },
        sistema ? el('select', { class: 'input', id: 'pc-cliente', style: { width: 'auto' }, onchange: ev => { clienteSel = ev.target.value; recarregar(); } },
          clientes.map(c => el('option', { value: c.id, selected: c.id === clienteSel }, c.nome))) : null,
        el('div', { class: 'contadores', id: 'pc-resumo', style: { display: 'flex', gap: '8px', margin: '0' } },
          el('span', { class: 'badge badge-yellow' }, `${r.pendentes} para conferir`),
          el('span', { class: 'badge badge-gray' }, `${r.semLink} sem link`),
          el('span', { class: 'badge badge-blue' }, `${r.aguardando} aguardando`),
          el('span', { class: 'badge badge-green' }, `${r.conferidos} conferidas`)))));
    cont.appendChild(el('div', { class: 'usu-filtros' },
      el('div', { style: { display: 'flex', gap: '6px', flexWrap: 'wrap' }, id: 'pc-abas' }, ABAS_PRECHECKIN.map(([ch, rot]) => el('button', { type: 'button', class: 'btn btn-sm ' + (filtro.aba === ch && !filtro.texto ? 'btn-primary' : 'btn-secondary'), dataset: { aba: ch }, onclick: () => { filtro.aba = ch; filtro.texto = ''; desenhar(); } }, rot))),
      el('input', { type: 'search', class: 'input', id: 'pc-busca', placeholder: 'Quarto, número ou titular', value: filtro.texto, oninput: ev => { filtro.texto = ev.target.value; desenharLista(); } })));
    cont.appendChild(el('div', { id: 'pc-lista' }));
    desenharLista();
  }

  function desenharLista() {
    const alvo = cont.querySelector('#pc-lista'); if (!alvo) return;
    alvo.replaceChildren();
    const todas = classificarPainel(linhas, agoraISO());
    const lista = filtrarPainel(todas, filtro);
    if (!lista.length) { alvo.appendChild(el('p', { class: 'vazio', id: 'pc-vazio' }, todas.length ? (filtro.texto ? 'Nada com esse filtro.' : 'Nada nesta aba.') : 'Nenhuma reserva confirmada por vir.')); return; }
    for (const l of lista) alvo.appendChild(cartao(l));
  }

  function cartao(l) {
    const e = ESTADOS[l.estado];
    const acoes = acoesDaLinha(l, { podeEditar, souDono });
    const detalhe = l.estado === 'preenchido' ? `${l.envio_pessoas} pessoa(s) enviadas ${dataCurta(String(l.envio_enviado_em || '').slice(0, 10), hoje())}`
      : l.estado === 'link' ? `link gerado ${dataCurta(String(l.convite_criado_em || '').slice(0, 10), hoje())} · vale até ${dataCurta(l.check_out, hoje())}`
      : l.estado === 'recusado' ? `recusado: ${l.envio_observacao || ''}`
      : l.estado === 'conferido' ? `${l.envio_pessoas} pessoa(s) conferidas`
      : l.estado === 'vencido' ? 'o link venceu sem uso' : '';
    return el('div', { class: 'card', style: { padding: '12px 14px', marginBottom: '10px' }, dataset: { reserva: l.reserva_id, estado: l.estado } },
      el('div', { style: { display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' } },
        el('span', { class: 'usu-nome', style: { fontSize: '16px' } }, 'Quarto ' + l.quarto),
        el('span', { class: 'badge ' + e.classe }, e.rotulo),
        el('span', { class: 'usu-email' }, `${dataCurta(l.check_in, hoje())} → ${dataCurta(l.check_out, hoje())} · ${noites(l.check_in, l.check_out)} noite(s)`),
        l.imobiliaria ? el('span', { class: 'usu-email' }, l.imobiliaria) : null,
        l.numero ? el('span', { class: 'usu-email' }, `nº ${l.numero}`) : null,
        el('span', { style: { flex: '1' } }),
        acoes.includes('conferir') ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', dataset: { conferir: l.reserva_id }, onclick: () => modalConferir(l) }, 'Conferir') : null,
        acoes.includes('gerar') ? el('button', { type: 'button', class: 'btn btn-primary btn-sm', dataset: { gerar: l.reserva_id }, onclick: () => gerarLink(l) }, icone('ic-plus'), 'Gerar link') : null,
        acoes.includes('novo_link') ? el('button', { type: 'button', class: 'btn btn-secondary btn-sm', dataset: { gerar: l.reserva_id }, onclick: () => gerarLink(l, true) }, 'Novo link') : null),
      el('div', { class: 'usu-email', style: { marginTop: '6px' } }, [l.titular ? `titular na ficha: ${l.titular} (${l.hospedes} cadastrado(s))` : (l.hospedes ? `${l.hospedes} cadastrado(s)` : 'ninguém cadastrado'), detalhe].filter(Boolean).join(' · ')),
      // 0021: o que o hóspede disse da chegada, já na reserva
      (l.chegada_prevista || l.placa_veiculo) ? el('div', { class: 'usu-email', style: { marginTop: '2px' }, dataset: { chegada: l.chegada_prevista || '' } },
        [l.chegada_prevista ? `chega ${rotuloFaixa(l.chegada_prevista).toLowerCase()}` : null, l.placa_veiculo ? `placa ${l.placa_veiculo}` : null].filter(Boolean).join(' · ')) : null);
  }

  // ── gerar o link ───────────────────────────────────────────────────
  async function gerarLink(l, substitui = false) {
    if (substitui) {
      const sim = await confirmar({ titulo: `Gerar um novo link para o quarto ${l.quarto}?`, texto: 'O link anterior deixa de valer na hora.', sim: 'Gerar' });
      if (!sim) return;
    }
    let token;
    try { token = await auth.rpc('criar_convite_precheckin', { p_reserva_id: l.reserva_id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    const link = montarLink(location.origin, token);
    const campo = el('input', { class: 'input', id: 'pc-link', value: link, readonly: true, onclick: ev => ev.target.select() });
    async function copiar() {
      try { await navigator.clipboard.writeText(link); aviso('Link copiado.'); } catch (_) { campo.select(); aviso('Selecione e copie o link.', 'erro'); }
    }
    abrirModal([
      el('h2', { class: 'modal-title' }, `Link do pré-check-in · quarto ${l.quarto}`),
      el('p', { style: { fontSize: '14px', color: '#374151', margin: '0 0 12px' } }, `Vale até ${dataCurta(l.check_out, hoje())}, uma vez só. Mande ao hóspede por e-mail ou pelo canal que preferir — este link só aparece agora.`),
      el('div', { class: 'form-row' }, campo),
      el('div', { style: { display: 'flex', gap: '10px' } },
        el('button', { type: 'button', class: 'btn btn-primary', id: 'btn-copiar', style: { flex: '1' }, onclick: copiar }, 'Copiar link'),
        el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: () => { fecharModal(); recarregar(); } }, 'Fechar')),
    ]);
    campo.select();
  }

  // ── conferir ───────────────────────────────────────────────────────
  async function modalConferir(l) {
    let dados;
    try { dados = await auth.rpc('precheckin_envio', { p_envio: l.envio_id, ...pc() }); } catch (e) { aviso(e.message, 'erro'); return; }
    const pessoas = (dados || []).filter(x => x.origem === 'envio');
    const fichas = (dados || []).filter(x => x.origem === 'ficha');
    const lado = compararEnvio(pessoas, fichas);
    const erro = el('div', { id: 'modal-erro', class: 'alert alert-error hidden', role: 'alert' });
    async function conferir() {
      let n;
      try { n = await auth.rpc('conferir_precheckin', { p_envio: l.envio_id, ...pc() }); } catch (e) { erro.textContent = e.message; erro.classList.remove('hidden'); return; }
      fecharModal(); aviso(`Conferido · ${n} pessoa(s) na ficha do quarto ${l.quarto}.`); recarregar();
    }
    abrirModal([
      el('h2', { class: 'modal-title' }, `Conferir · quarto ${l.quarto} · ${dataCurta(l.check_in, hoje())}`),
      erro,
      el('p', { style: { fontSize: '13px', color: '#6b7280', margin: '0 0 10px' } }, 'Compare com o documento apresentado. Quem já está na ficha com o mesmo documento é atualizado, não duplicado.'),
      el('div', { id: 'pc-pessoas' }, lado.map(p => el('div', { class: 'ficha-item', style: { display: 'block', padding: '8px 0' }, dataset: { pessoa: p.ordem, novo: String(p.novo) } },
        el('div', { style: { display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' } },
          el('span', { class: 'usu-nome' }, p.nome),
          p.principal ? el('span', { class: 'badge badge-blue' }, 'titular') : null,
          el('span', { class: 'badge ' + (p.novo ? 'badge-yellow' : 'badge-green') }, p.novo ? 'novo na ficha' : 'já na ficha' + (p.mudou.length ? ` · atualiza ${p.mudou.join(', ')}` : ''))),
        el('div', { class: 'usu-email' }, [p.documento ? `${p.tipo_documento || 'Doc'} ${formatarDocumento(p.documento, p.tipo_documento)}`
            : (p.jaNaFicha && p.jaNaFicha.documento ? `${p.jaNaFicha.tipo_documento || 'Doc'} ${formatarDocumento(p.jaNaFicha.documento, p.jaNaFicha.tipo_documento)} (da ficha, não redigitado)` : 'sem documento'),
          p.data_nascimento ? `${idade(p.data_nascimento, hoje())} anos` : null, p.telefone, p.email].filter(Boolean).join(' · ')),
        resumoViagem(p) ? el('div', { class: 'usu-email', dataset: { viagem: p.ordem } }, resumoViagem(p)) : null,
        // 0063: os campos da FNRH (0062), para a recepção conferir o que vai virar ficha
        (p.sexo || p.raca || p.deficiencia || p.nome_social || p.bairro || p.complemento || p.legalmente_incapaz) ? el('div', { class: 'usu-email', dataset: { fnrh: p.ordem } },
          [[rotuloSexo(p.sexo), p.genero_descricao].filter(Boolean).join(' — ') || null, p.raca ? `raça/cor: ${rotuloRaca(p.raca).toLowerCase()}` : null,
           // 0065: deficiência (e o tipo), como o hóspede declarou
           p.deficiencia ? `deficiência: ${[rotuloDeficiencia(p.deficiencia), rotuloTipoDeficiencia(p.tipo_deficiencia)].filter(Boolean).join(' — ').toLowerCase()}` : null,
           p.nome_social ? `nome social: ${p.nome_social}` : null, [p.complemento, p.bairro].filter(Boolean).join(', ') || null,
           p.legalmente_incapaz ? 'legalmente incapaz' : null].filter(Boolean).join(' · ')) : null))),
      (pessoas[0] && (pessoas[0].chegada_prevista || pessoas[0].placa_veiculo)) ? el('div', { class: 'usu-grupo', id: 'pc-chegada' },
        ['Chegada: ' + (pessoas[0].chegada_prevista ? rotuloFaixa(pessoas[0].chegada_prevista).toLowerCase() : 'sem hora informada'), pessoas[0].placa_veiculo ? `placa ${pessoas[0].placa_veiculo}` : null].filter(Boolean).join(' · ')) : null,
      fichas.length ? el('div', { class: 'usu-grupo' }, `Na ficha hoje: ${fichas.map(h => h.nome).join(', ')}`) : null,
      podeEditar ? el('div', { style: { marginTop: '14px' } },
        el('div', { style: { display: 'flex', gap: '10px', marginBottom: '10px' } },
          el('button', { type: 'button', class: 'btn btn-verde', id: 'btn-conferir', style: { flex: '1' }, onclick: conferir }, 'Conferido — vira ficha'),
          el('button', { type: 'button', class: 'btn btn-secondary', style: { flex: '1' }, onclick: fecharModal }, 'Depois')),
        el('div', { id: 'pc-corrigir', style: { fontSize: '12px', color: '#6b7280' } }, 'Algum dado errado? Confira assim mesmo e corrija a ficha na hora do check-in.')) : null,
    ]);
  }

  recarregar();
  return () => { vivo = false; };
}
